import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryConfig } from '@src/lib/api'
import { ResponseDTO } from '@src/types/api'
import { Note, NoteChangeDTO, NoteCreateDTO, NoteSortOrderUpdateDTO } from '@src/types/notes'

export const notesApi = createApi({
  reducerPath: 'notesApi',
  baseQuery: baseQueryConfig('/notes'),
  tagTypes: [ 'NOTES', 'NOTE' ],
  endpoints: build => ({
    getNotes: build.query<ResponseDTO<Note[]>, void>({
      query: () => ({
        url: '/'
      }),
      providesTags: (r: ResponseDTO<Note[]> | undefined, error) => ([
        ...((!r || error) ? [] : r.result).map(({ _id: id }) => ({ type: 'NOTES' as const, id })),
        { type: 'NOTES', id: 'LIST' }
      ])
    }),
    getNote: build.query<Note & {
      prevNoteId: string | null;
      nextNoteId: string | null;
    }, string>({
      query: (id) => ({
        url: `/${id}`
      }),
      providesTags: (r: Note | undefined, error) => (
        !error && r ? [{ type: 'NOTES', id: r._id }] : []
      )
    }),
    creteNote: build.mutation<Note, NoteCreateDTO>({
      invalidatesTags: [{ type: 'NOTES', id: 'LIST' }],
      query: (body) => ({
        method: 'POST',
        url: '/',
        body,
      })
    }),
    updateNote: build.mutation<Note, NoteChangeDTO>({
      query: ({ id , ...body }) => ({
        method: 'POST',
        url: `/${id}`,
        body,
      }),
      invalidatesTags: (result, error) => ([
        { type: 'NOTES', id: 'LIST' },
        result && !error ? { type: 'NOTES', id: result._id } : undefined,
      ]),
    }),
    updateSortOrder: build.mutation<Note, NoteSortOrderUpdateDTO>({
      query: (body) => ({
        method: 'POST',
        url: '/update-sort-order',
        body,
      }),
      invalidatesTags: (result, error) => ([
        { type: 'NOTES', id: 'LIST' },
        result && !error ? { type: 'NOTES', id: result._id } : undefined,
      ]),
    }),
    deleteNote: build.mutation<Note, string>({
      query: (id) => ({
        method: 'DELETE',
        url: `/${id}`,
      }),
      invalidatesTags: () => ([
        { type: 'NOTES', id: 'LIST' },
      ]),
    }),
  })
})

export const {
  useGetNotesQuery,
  useGetNoteQuery,
  useCreteNoteMutation,
  useUpdateNoteMutation,
  useUpdateSortOrderMutation,
  useDeleteNoteMutation,
} = notesApi